/**
 * Public marketing site configuration shared by the React app (PageSeo, routing,
 * language sync) and the build step (scripts/generate-seo-pages.js), so the
 * crawlable HTML, the sitemap and the runtime head always agree.
 *
 * URL scheme: the default language lives at the root (`/`, `/privacy`) and doubles as
 * hreflang x-default; every other language is prefixed (`/en`, `/en/privacy`).
 */
export const MARKETING_ORIGIN = 'https://salovare.com';
export const MARKETING_OG_IMAGE_WIDTH = 1200;
export const MARKETING_OG_IMAGE_HEIGHT = 630;

export const MARKETING_DEFAULT_LANGUAGE = 'pt';
export const MARKETING_LANGUAGES = ['pt', 'en', 'es'];
export const MARKETING_PREFIXED_LANGUAGES = MARKETING_LANGUAGES.filter(
  (lang) => lang !== MARKETING_DEFAULT_LANGUAGE
);

export const MARKETING_OG_LOCALES = {
  pt: 'pt_PT',
  en: 'en_GB',
  es: 'es_ES',
};

/** Indexable pages, in sitemap order. `path` is the language-neutral path. */
export const MARKETING_PAGES = [
  { path: '/', changefreq: 'weekly', priority: '1.0' },
  { path: '/register-store', changefreq: 'monthly', priority: '0.8' },
  { path: '/privacy', changefreq: 'yearly', priority: '0.3' },
  { path: '/cookies', changefreq: 'yearly', priority: '0.3' },
  { path: '/terms', changefreq: 'yearly', priority: '0.3' },
];

const MARKETING_PAGE_PATHS = new Set(MARKETING_PAGES.map((page) => page.path));

const normalizePath = (path = '/') => {
  const withSlash = path.startsWith('/') ? path : `/${path}`;
  return withSlash.length > 1 ? withSlash.replace(/\/+$/, '') : withSlash;
};

export const getMarketingUrl = (path = '/') => `${MARKETING_ORIGIN}${normalizePath(path)}`;

/** 1200×630 social preview per language (public/og-image[-lang].png). */
export const getMarketingOgImageUrl = (lang = MARKETING_DEFAULT_LANGUAGE) =>
  getMarketingUrl(lang === MARKETING_DEFAULT_LANGUAGE ? '/og-image.png' : `/og-image-${lang}.png`);

/** `/privacy` + `en` → `/en/privacy`; `/` + `en` → `/en`; default language stays unprefixed. */
export const localizeMarketingPath = (path, lang) => {
  const basePath = normalizePath(path);
  if (!MARKETING_PREFIXED_LANGUAGES.includes(lang)) {
    return basePath;
  }
  return basePath === '/' ? `/${lang}` : `/${lang}${basePath}`;
};

export const getLocalizedMarketingUrl = (path, lang) => getMarketingUrl(localizeMarketingPath(path, lang));

/**
 * Splits a pathname into its language and language-neutral path.
 * `/en/privacy` → { lang: 'en', basePath: '/privacy', prefixed: true }.
 * `isPage` tells whether basePath is one of the indexable marketing pages.
 */
export const parseMarketingPath = (pathname = '/') => {
  const path = normalizePath(pathname);
  const [, firstSegment = ''] = path.split('/');
  const prefixed = MARKETING_LANGUAGES.includes(firstSegment);
  const basePath = prefixed ? normalizePath(path.slice(firstSegment.length + 1) || '/') : path;

  return {
    lang: prefixed ? firstSegment : MARKETING_DEFAULT_LANGUAGE,
    basePath,
    prefixed,
    isPage: MARKETING_PAGE_PATHS.has(basePath),
  };
};

/** hreflang alternates for a page, including x-default (the unprefixed default language). */
export const getMarketingAlternates = (path) => [
  ...MARKETING_LANGUAGES.map((lang) => ({ hrefLang: lang, href: getLocalizedMarketingUrl(path, lang) })),
  { hrefLang: 'x-default', href: getLocalizedMarketingUrl(path, MARKETING_DEFAULT_LANGUAGE) },
];
